import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, GripVertical, ImagePlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";

const EMPTY_HISTORY = { titulo: "", descricao: "", data_ref: "", categoria: "Geral", url: "", ordem: 0, active: true };
const EMPTY_LINK = { titulo: "", descricao: "", url: "", categoria: "Geral", ordem: 0, active: true };
const EMPTY_PHOTO = { titulo: "", legenda: "", ano: "", url: "", categoria: "Memórias", ordem: 0, active: true, album_photo_id: null };

function ImageUpload({ url, testId, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagem enviada.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => send(e.target.files?.[0])} data-testid={`${testId}-file`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={testId}>
        <ImagePlus size={14} className="mr-1" /> {busy ? "Enviando..." : "Imagem"}
      </Button>
    </>
  );
}

function StatusBadge({ active }) {
  return active ? (
    <Badge className="bg-primary text-primary-foreground text-[10px]">Ativo</Badge>
  ) : (
    <Badge variant="secondary" className="text-[10px]">Inativo</Badge>
  );
}

function AcervoPicker({ open, onClose, onPick }) {
  const [albums, setAlbums] = useState(null);
  useEffect(() => {
    if (!open) return;
    setAlbums(null);
    Promise.all([
      api.get("/public/albums", { params: { tipo: "historico" } }),
      api.get("/public/albums", { params: { tipo: "momentos" } }),
    ])
      .then(([h, m]) => setAlbums([...h.data, ...m.data]))
      .catch(() => setAlbums([]));
  }, [open]);
  const comFotos = (albums || []).filter((a) => a.fotos.length > 0);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="acervo-picker-dialog">
        <DialogHeader>
          <DialogTitle>Escolher foto do acervo</DialogTitle>
          <DialogDescription className="sr-only">Selecione uma foto de álbum publicado para vincular à memória.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          A foto original do álbum será reutilizada nas Memórias do IMJ — sem duplicar arquivos.
        </p>
        {albums === null && <p className="text-sm text-muted-foreground">Carregando acervo...</p>}
        {albums !== null && comFotos.length === 0 && (
          <p className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="acervo-picker-empty">
            Nenhum álbum publicado com fotos. Publique álbuns em Fotos para reutilizá-los aqui.
          </p>
        )}
        <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-1">
          {comFotos.map((a) => (
            <div key={a.id} data-testid={`acervo-picker-album-${a.id}`}>
              <p className="text-sm font-semibold">
                {a.titulo} {a.ano && <span className="text-xs font-normal text-muted-foreground">· {a.ano}</span>}
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {a.fotos.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onPick(f, a)}
                    className="group border border-border text-left transition-colors hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid={`acervo-picker-photo-${f.id}`}
                  >
                    <img src={f.url} alt={f.titulo || "Foto do acervo"} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                    <span className="block truncate px-1.5 py-1 text-[10px] text-muted-foreground">{f.titulo || "Sem título"}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MemoriesPreview({ open, onClose }) {
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!open) return;
    setData(null);
    setIndex(0);
    api.get("/public/memories").then((r) => setData(r.data)).catch(() => setData({ fotos: [] }));
  }, [open]);
  const fotos = data?.fotos || [];
  const current = fotos.length ? fotos[Math.min(index, fotos.length - 1)] : null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="memories-preview-dialog">
        <DialogHeader>
          <DialogTitle>Prévia — Memórias do IMJ</DialogTitle>
          <DialogDescription className="sr-only">Pré-visualização administrativa do carrossel da página inicial.</DialogDescription>
        </DialogHeader>
        <p className="border border-primary/40 bg-primary/10 p-2 text-xs" data-testid="memories-preview-note">
          Prévia administrativa — exibe as fotos realmente ativas, na mesma ordem da página inicial. Nada aqui publica ou altera o site.
        </p>
        {data === null && <p className="text-sm text-muted-foreground">Carregando...</p>}
        {data !== null && fotos.length === 0 && (
          <p className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="memories-preview-empty">
            Nenhuma memória ativa no momento. Destaque fotos em <strong>Fotos &amp; Álbuns</strong> ou use "Escolher do acervo".
          </p>
        )}
        {current && (
          <div data-testid="memories-preview-frame">
            <div className="relative aspect-[16/9] w-full overflow-hidden border border-border bg-zinc-900">
              <img src={current.imagem_url} alt={current.legenda || current.titulo} className="h-full w-full object-cover" data-testid="memories-preview-image" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="font-heading text-base font-bold text-white" data-testid="memories-preview-caption">
                  {current.titulo}{current.ano ? ` · ${current.ano}` : ""}
                </p>
                {current.legenda && <p className="mt-1 text-xs text-zinc-300" data-testid="memories-preview-legenda">{current.legenda}</p>}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={fotos.length < 2} onClick={() => setIndex((index - 1 + fotos.length) % fotos.length)} data-testid="memories-preview-prev">
                <ChevronLeft size={14} className="mr-1" /> Anterior
              </Button>
              <span className="text-xs text-muted-foreground" data-testid="memories-preview-position">Foto {index + 1} de {fotos.length}</span>
              <Button variant="outline" size="sm" disabled={fotos.length < 2} onClick={() => setIndex((index + 1) % fotos.length)} data-testid="memories-preview-next">
                Próxima <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SiteContent() {
  const [history, setHistory] = useState([]);
  const [links, setLinks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [intervalSec, setIntervalSec] = useState(10);
  const [dialog, setDialog] = useState(null); // { kind: "history"|"link"|"photo", item? }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // { kind, item }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    const [h, l, p, c] = await Promise.all([
      api.get("/site-content/history-items"),
      api.get("/site-content/daily-links"),
      api.get("/site-content/memory-photos"),
      api.get("/site-content/memories-config"),
    ]);
    setHistory(h.data);
    setLinks(l.data);
    setPhotos(p.data);
    setIntervalSec(c.data.interval_seconds);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const openDialog = (kind, item) => {
    setDialog({ kind, item: item || null });
    if (kind === "history") setForm(item ? { titulo: item.titulo, descricao: item.descricao || "", data_ref: item.data_ref || "", categoria: item.categoria, url: item.url || "", ordem: item.ordem ?? 0, active: item.active } : EMPTY_HISTORY);
    if (kind === "link") setForm(item ? { titulo: item.titulo, descricao: item.descricao || "", url: item.url, categoria: item.categoria, ordem: item.ordem ?? 0, active: item.active } : EMPTY_LINK);
    if (kind === "photo") setForm(item ? { titulo: item.titulo, legenda: item.legenda || "", ano: item.ano || "", url: item.url || "", categoria: item.categoria, ordem: item.ordem ?? 0, active: item.active, album_photo_id: item.album_photo_id || null } : EMPTY_PHOTO);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const base = { history: "/site-content/history-items", link: "/site-content/daily-links", photo: "/site-content/memory-photos" }[dialog.kind];
    try {
      const payload = { ...form, ordem: Number(form.ordem) || 0, url: form.url || null, ...(dialog.kind === "photo" ? { album_photo_id: form.album_photo_id || null } : {}) };
      if (dialog.item) await api.put(`${base}/${dialog.item.id}`, payload);
      else await api.post(base, payload);
      toast.success("Registro salvo.");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const pickFromAcervo = (foto, album) => {
    setPickerOpen(false);
    setDialog({ kind: "photo", item: null });
    setForm({ ...EMPTY_PHOTO, titulo: foto.titulo || album.titulo, legenda: foto.legenda || "", ano: foto.ano || album.ano || "", album_photo_id: foto.id });
  };

  const toggleActive = async (kind, item) => {
    const base = { history: "/site-content/history-items", link: "/site-content/daily-links", photo: "/site-content/memory-photos" }[kind];
    const payload = {
      titulo: item.titulo,
      descricao: item.descricao || null,
      categoria: item.categoria,
      url: item.url || null,
      ordem: item.ordem ?? 0,
      active: !item.active,
      ...(kind === "history" ? { data_ref: item.data_ref || null } : {}),
      ...(kind === "photo" ? { legenda: item.legenda || null, ano: item.ano || null } : {}),
    };
    try {
      await api.put(`${base}/${item.id}`, payload);
      toast.success(item.active ? "Desativado (oculto do site)." : "Ativado (visível no site).");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    const base = { history: "/site-content/history-items", link: "/site-content/daily-links", photo: "/site-content/memory-photos" }[deleting.kind];
    try {
      await api.delete(`${base}/${deleting.item.id}`);
      toast.success("Registro removido.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  const saveInterval = async () => {
    try {
      await api.put("/site-content/memories-config", { interval_seconds: Number(intervalSec) });
      toast.success("Intervalo atualizado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const ativasMemorias = photos.filter((p) => p.active && p.imagem_url).length;

  const persistOrder = async (ordered) => {
    setReordering(true);
    try {
      await api.put("/site-content/memory-photos/reorder", { ids: ordered.map((p) => p.id) });
      toast.success("Ordem das Memórias atualizada.");
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    } finally {
      setReordering(false);
    }
  };

  const movePhoto = (id, dir) => {
    const idx = photos.findIndex((p) => p.id === id);
    const alvo = idx + dir;
    if (idx < 0 || alvo < 0 || alvo >= photos.length) return;
    const nova = [...photos];
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    setPhotos(nova);
    persistOrder(nova);
  };

  const dropPhoto = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const nova = [...photos];
    const from = nova.findIndex((p) => p.id === dragId);
    const to = nova.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0) return;
    const [item] = nova.splice(from, 1);
    nova.splice(to, 0, item);
    setPhotos(nova);
    setDragId(null);
    setDragOverId(null);
    persistOrder(nova);
  };

  const RowActions = ({ kind, item, extra }) => (
    <div className="flex items-center gap-1">
      {extra}
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(kind, item)} data-testid={`${kind}-edit-${item.id}`}>
        <Pencil size={13} />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleting({ kind, item })} data-testid={`${kind}-delete-${item.id}`}>
        <Trash2 size={13} className="text-destructive" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="site-content-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Conteúdo do Site</h1>
        <p className="mt-1 text-sm text-muted-foreground">Nossa História, Links do Dia a Dia e fotos das Memórias do IMJ.</p>
      </div>

      <Tabs defaultValue="historia">
        <TabsList data-testid="site-content-tabs">
          <TabsTrigger value="historia" data-testid="tab-historia">Nossa História</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links">Links do Dia a Dia</TabsTrigger>
          <TabsTrigger value="memorias" data-testid="tab-memorias">Memórias (fotos)</TabsTrigger>
        </TabsList>

        <TabsContent value="historia" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openDialog("history", null)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="history-new-button">
              Novo registro
            </Button>
          </div>
          <div className="border border-border bg-white" data-testid="history-table">
            {history.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="history-empty">Nenhum registro histórico cadastrado.</p>}
            {history.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-3 border-b border-border p-3 last:border-b-0" data-testid={`history-row-${i.id}`}>
                {i.capa_url && <img src={i.capa_url} alt="" className="h-10 w-16 object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{i.titulo} {i.data_ref && <span className="text-xs text-muted-foreground">· {i.data_ref}</span>}</p>
                  <p className="text-xs text-muted-foreground">{i.categoria} · ordem {i.ordem}{i.url && " · com link"}</p>
                </div>
                <StatusBadge active={i.active} />
                <button type="button" onClick={() => toggleActive("history", i)} className="text-xs font-medium text-primary hover:underline" data-testid={`history-toggle-${i.id}`}>
                  {i.active ? "Desativar" : "Ativar"}
                </button>
                <RowActions kind="history" item={i} extra={<ImageUpload url={`/site-content/history-items/${i.id}/cover`} testId={`history-cover-${i.id}`} onDone={load} />} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="links" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openDialog("link", null)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="link-new-button">
              Novo link
            </Button>
          </div>
          <div className="border border-border bg-white" data-testid="links-table">
            {links.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="links-empty-admin">Nenhum link cadastrado.</p>}
            {links.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 border-b border-border p-3 last:border-b-0" data-testid={`link-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{l.titulo}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    {l.categoria} · ordem {l.ordem} · <ExternalLink size={11} /> {l.url}
                  </p>
                </div>
                <StatusBadge active={l.active} />
                <button type="button" onClick={() => toggleActive("link", l)} className="text-xs font-medium text-primary hover:underline" data-testid={`link-toggle-${l.id}`}>
                  {l.active ? "Desativar" : "Ativar"}
                </button>
                <RowActions kind="link" item={l} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="memorias" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border border-border bg-white p-3" data-testid="memories-summary">
            <p className="text-sm" data-testid="memories-active-count">
              <strong>{ativasMemorias}</strong> memória(s) ativa(s) no carrossel da página inicial
            </p>
            <p className="text-xs text-muted-foreground" data-testid="memories-total-count">
              {photos.length} registro(s) no total · Fotos do acervo sem destaque não aparecem aqui — destaque-as em Fotos &amp; Álbuns.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intervalo da troca automática (segundos)</Label>
                <Input type="number" min={3} max={120} value={intervalSec} onChange={(e) => setIntervalSec(e.target.value)} className="w-28" data-testid="memories-interval-input" />
              </div>
              <Button variant="outline" onClick={saveInterval} data-testid="memories-interval-save">Salvar</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="memories-preview-button">
                Pré-visualizar carrossel
              </Button>
              <Button variant="outline" onClick={() => setPickerOpen(true)} data-testid="photo-pick-acervo-button">
                Escolher do acervo
              </Button>
              <Button onClick={() => openDialog("photo", null)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="photo-new-button">
                Nova foto
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Somente fotos <strong>ativas e com imagem</strong> (enviada ou vinculada ao acervo) aparecem na página inicial. Fotos do acervo não são duplicadas. <strong>Arraste</strong> uma linha ou use as <strong>setas</strong> para definir a ordem do carrossel.</p>
          <div className="border border-border bg-white" data-testid="photos-table">
            {photos.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="photos-empty">Nenhuma foto cadastrada — a área "Memórias do IMJ" fica oculta na página inicial.</p>}
            {photos.map((p, idx) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => setDragId(p.id)}
                onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                onDragOver={(e) => { e.preventDefault(); setDragOverId(p.id); }}
                onDragLeave={() => setDragOverId((cur) => (cur === p.id ? null : cur))}
                onDrop={(e) => { e.preventDefault(); dropPhoto(p.id); }}
                className={`flex flex-wrap items-center gap-3 border-b border-border p-3 transition-colors last:border-b-0 ${dragId === p.id ? "opacity-50" : ""} ${dragOverId === p.id && dragId !== p.id ? "bg-primary/10 outline-1 outline-primary" : ""}`}
                data-testid={`photo-row-${p.id}`}
              >
                <span className="cursor-grab text-muted-foreground" title="Arraste para reordenar" aria-hidden="true">
                  <GripVertical size={15} />
                </span>
                {p.imagem_url ? (
                  <img src={p.imagem_url} alt="" className="h-10 w-16 object-cover" />
                ) : (
                  <span className="flex h-10 w-16 items-center justify-center border border-dashed border-border text-[10px] text-muted-foreground">sem imagem</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{p.titulo} {p.ano && <span className="text-xs text-muted-foreground">· {p.ano}</span>}</p>
                  <p className="text-xs text-muted-foreground">posição {idx + 1}{p.legenda && ` · ${p.legenda}`}</p>
                </div>
                <div className="flex flex-col" data-testid={`photo-reorder-${p.id}`}>
                  <button type="button" aria-label={`Mover ${p.titulo} para cima`} disabled={idx === 0 || reordering} onClick={() => movePhoto(p.id, -1)} className="text-muted-foreground transition-colors hover:text-primary disabled:opacity-30" data-testid={`photo-move-up-${p.id}`}>
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" aria-label={`Mover ${p.titulo} para baixo`} disabled={idx === photos.length - 1 || reordering} onClick={() => movePhoto(p.id, 1)} className="text-muted-foreground transition-colors hover:text-primary disabled:opacity-30" data-testid={`photo-move-down-${p.id}`}>
                    <ChevronDown size={14} />
                  </button>
                </div>
                <StatusBadge active={p.active} />
                {p.album_photo_id && <Badge variant="outline" className="text-[10px]" data-testid={`photo-acervo-badge-${p.id}`}>Acervo</Badge>}
                <button type="button" onClick={() => toggleActive("photo", p)} className="text-xs font-medium text-primary hover:underline" data-testid={`photo-toggle-${p.id}`}>
                  {p.active ? "Desativar" : "Ativar"}
                </button>
                <RowActions kind="photo" item={p} extra={<ImageUpload url={`/site-content/memory-photos/${p.id}/image`} testId={`photo-image-${p.id}`} onDone={load} />} />
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="site-content-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.item ? "Editar" : "Novo"} {dialog?.kind === "history" ? "registro histórico" : dialog?.kind === "link" ? "link" : "foto de memória"}</DialogTitle>
            <DialogDescription className="sr-only">Formulário de cadastro e edição de conteúdo do site.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="dialog-titulo" />
              </div>
              {dialog.kind === "photo" ? (
                <div className="space-y-2">
                  <Label>Legenda</Label>
                  <Textarea value={form.legenda} onChange={(e) => setForm({ ...form, legenda: e.target.value })} data-testid="dialog-legenda" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="dialog-descricao" />
                </div>
              )}
              {dialog.kind === "photo" && form.album_photo_id && (
                <p className="border border-primary/40 bg-primary/10 p-2 text-xs" data-testid="dialog-acervo-note">
                  Imagem vinculada do acervo — o arquivo original do álbum será exibido, sem duplicar fotos. Para substituir, salve e envie uma imagem própria na listagem.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {dialog.kind === "history" && (
                  <div className="space-y-2">
                    <Label>Ano/Data</Label>
                    <Input value={form.data_ref} onChange={(e) => setForm({ ...form, data_ref: e.target.value })} placeholder="Ex.: 2015" data-testid="dialog-data-ref" />
                  </div>
                )}
                {dialog.kind === "photo" && (
                  <div className="space-y-2">
                    <Label>Ano</Label>
                    <Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} placeholder="Ex.: 2015" data-testid="dialog-ano" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="dialog-categoria" />
                </div>
                <div className="space-y-2">
                  <Label>Ordem</Label>
                  <Input type="number" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} data-testid="dialog-ordem" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>URL {dialog.kind === "link" ? "" : "(opcional)"}</Label>
                <Input type="url" required={dialog.kind === "link"} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="dialog-url" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="dialog-active" />
                <Label>Ativo (visível no site)</Label>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="dialog-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AcervoPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={pickFromAcervo} />
      <MemoriesPreview open={previewOpen} onClose={() => setPreviewOpen(false)} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Remover registro"
        description={`Remover "${deleting?.item?.titulo}"?`}
        onConfirm={remove}
        testId="site-content-delete-confirm"
      />
    </div>
  );
}
